// Tiny hand-drawn-feel SVG birds for the proposal page.
// All paths are minimal — silhouettes, not illustrations. The point is character,
// not detail. Each bird has a tiny "head" group that can tilt on hover for life.
//
// Style notes:
//  - currentColor for stroke/fill so colors come from CSS
//  - Each bird is a self-contained <svg> so it can be dropped anywhere
//  - The blink animation is on .bird-eye via random delays per instance

import { useEffect, useState } from "react";

// ─── Perched bird sitting on the letterhead mark ──────────────────
// Side profile, small, looking right. Head tilts on a 7s loop.
export function PerchedBird({ className = "" }) {
  return (
    <svg
      className={`bird bird-perched ${className}`}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* body */}
      <path
        d="M8 18 C8 14, 12 11, 17 11 C22 11, 25 13, 25 17 C25 19, 23 21, 19 21 L11 21 C9 21, 8 20, 8 18 Z"
        fill="currentColor"
      />
      {/* tail */}
      <path d="M8 17 L4 14 L5 18 Z" fill="currentColor" />
      {/* head — wrapped so we can tilt it */}
      <g className="bird-head" style={{ transformOrigin: "20px 14px" }}>
        <circle cx="22" cy="13" r="4" fill="currentColor" />
        {/* beak */}
        <path d="M26 13 L29 13 L26 14.5 Z" fill="currentColor" />
        {/* eye — sits on top of head fill */}
        <circle className="bird-eye" cx="23" cy="12.5" r="0.7" fill="#08111c" />
      </g>
      {/* legs */}
      <line x1="13" y1="21" x2="13" y2="25" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <line x1="18" y1="21" x2="18" y2="25" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

// ─── Thrush — small, upright, "alert little fellow" ───────────────
export function ThrushBird({ className = "" }) {
  const [delay] = useState(() => Math.random() * 8 + 4); // 4-12s blink offset
  return (
    <svg
      className={`bird bird-thrush ${className}`}
      viewBox="0 0 40 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* upright plump body */}
      <ellipse cx="20" cy="26" rx="11" ry="13" fill="currentColor" />
      {/* tail down */}
      <path d="M16 38 L18 44 L23 38 Z" fill="currentColor" />
      {/* head */}
      <g className="bird-head" style={{ transformOrigin: "20px 16px" }}>
        <circle cx="20" cy="14" r="7" fill="currentColor" />
        {/* small pointy beak */}
        <path d="M27 14 L31 15 L27 16 Z" fill="currentColor" />
        {/* eye */}
        <circle
          className="bird-eye"
          cx="23"
          cy="12.5"
          r="1"
          fill="#08111c"
          style={{ animationDelay: `${delay}s` }}
        />
      </g>
      {/* feet hint */}
      <line x1="17" y1="38" x2="17" y2="42" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
      <line x1="22" y1="38" x2="22" y2="42" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
    </svg>
  );
}

// ─── Flycatcher — sleek, leaning forward, longer tail ─────────────
export function FlycatcherBird({ className = "" }) {
  const [delay] = useState(() => Math.random() * 10 + 5);
  return (
    <svg
      className={`bird bird-flycatcher ${className}`}
      viewBox="0 0 50 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* sleek body, leaning right */}
      <path
        d="M10 22 C10 16, 18 12, 28 13 C36 14, 40 17, 40 20 C40 23, 36 25, 28 25 L14 25 C11 25, 10 24, 10 22 Z"
        fill="currentColor"
      />
      {/* long elegant tail */}
      <path d="M10 22 L2 20 L4 24 L10 23 Z" fill="currentColor" />
      {/* wing detail — folded */}
      <path
        d="M18 17 C24 16, 30 18, 32 21 C28 21, 22 20, 18 17 Z"
        fill="currentColor"
        opacity="0.55"
      />
      {/* head */}
      <g className="bird-head" style={{ transformOrigin: "33px 14px" }}>
        <circle cx="34" cy="13" r="5" fill="currentColor" />
        {/* slim beak */}
        <path d="M39 13 L44 13.5 L39 14.5 Z" fill="currentColor" />
        <circle
          className="bird-eye"
          cx="36"
          cy="12"
          r="0.9"
          fill="#08111c"
          style={{ animationDelay: `${delay}s` }}
        />
      </g>
    </svg>
  );
}

// ─── Hornbill — big, unmistakable casque + huge curved beak ───────
export function HornbillBird({ className = "" }) {
  const [delay] = useState(() => Math.random() * 12 + 6);
  return (
    <svg
      className={`bird bird-hornbill ${className}`}
      viewBox="0 0 60 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* large rounded body */}
      <ellipse cx="22" cy="30" rx="15" ry="11" fill="currentColor" />
      {/* tail with character */}
      <path d="M7 28 L1 24 L3 32 L9 31 Z" fill="currentColor" />
      {/* wing */}
      <path
        d="M14 22 C20 19, 30 21, 32 27 C26 28, 18 27, 14 22 Z"
        fill="currentColor"
        opacity="0.5"
      />
      {/* head + the iconic casque + beak */}
      <g className="bird-head" style={{ transformOrigin: "32px 18px" }}>
        {/* head */}
        <circle cx="34" cy="17" r="7" fill="currentColor" />
        {/* casque — the bony top ridge that makes a hornbill a hornbill */}
        <path
          d="M28 13 C30 9, 38 8, 42 11 C44 12, 44 15, 42 16 L34 16 C32 16, 29 15, 28 13 Z"
          fill="currentColor"
        />
        {/* the big curved beak */}
        <path
          d="M40 16 C48 16, 55 18, 57 21 C55 22, 50 22, 44 21 C42 21, 40 20, 40 19 Z"
          fill="currentColor"
        />
        {/* beak under-line */}
        <path
          d="M40 19 C46 20, 52 21, 56 21"
          stroke="#08111c"
          strokeWidth="0.6"
          opacity="0.4"
          fill="none"
        />
        {/* eye */}
        <circle
          className="bird-eye"
          cx="35"
          cy="15.5"
          r="1"
          fill="#08111c"
          style={{ animationDelay: `${delay}s` }}
        />
      </g>
      {/* legs */}
      <line x1="18" y1="40" x2="18" y2="45" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
      <line x1="26" y1="40" x2="26" y2="45" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
    </svg>
  );
}

// ─── Drifting flock silhouettes for the hero atmosphere ───────────
// Three V-shaped birds that drift slowly across the hero. Very faint.
function FlockBird({ x, y, scale, delay, duration }) {
  return (
    <svg
      className="bird-flock-bird"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: `scale(${scale})`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
      }}
      viewBox="0 0 24 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* simple V with a slight curve — reads as "bird" instantly */}
      <path
        d="M2 10 Q 7 2, 12 6 Q 17 2, 22 10"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

export function HeroFlock() {
  // Three birds, each with its own drift timing.
  return (
    <div className="bird-flock" aria-hidden="true">
      <FlockBird x={5} y={20} scale={1} delay={0} duration={48} />
      <FlockBird x={-5} y={35} scale={0.7} delay={8} duration={56} />
      <FlockBird x={-15} y={55} scale={1.2} delay={16} duration={42} />
    </div>
  );
}

// ─── Random blink hook — staggered eye-blinks across all birds ────
// Useful if we want global coordination later. Currently each bird's
// blink delay is randomized at mount via useState.
export function useBlinkDelay(min = 4, max = 12) {
  const [delay] = useState(() => Math.random() * (max - min) + min);
  return delay;
}
